// ===========================================
// Void an invoice — required internal reason + optional family email
// ===========================================
// md/plan/invoice-void-reason.md. Replaces the plain "Are you sure?" confirm,
// which never collected a reason (the API accepted one; the UI never sent it).
//
// - The reason is REQUIRED (3–500 characters) and INTERNAL: a tax record the
//   family never sees (D2, D4).
// - "Email the family" is OFF by default (D3). The email says no payment is
//   due and carries no reason.
// - A void is final (D8), and the copy says so.

import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

export const VOID_REASON_MIN = 3;
export const VOID_REASON_MAX = 500;

export interface VoidInvoiceDialogProps {
  open: boolean;
  invoiceNumber: string;
  loading?: boolean;
  onConfirm: (data: { reason: string; notifyFamily: boolean }) => void;
  onClose: () => void;
}

export default function VoidInvoiceDialog({
  open,
  invoiceNumber,
  loading = false,
  onConfirm,
  onClose,
}: VoidInvoiceDialogProps) {
  const [reason, setReason] = useState('');
  const [notifyFamily, setNotifyFamily] = useState(false);

  // Fresh every time it opens: a half-typed reason must not leak onto another invoice.
  useEffect(() => {
    if (open) {
      setReason('');
      setNotifyFamily(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= VOID_REASON_MIN && trimmed.length <= VOID_REASON_MAX;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Void invoice {invoiceNumber}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The invoice stays on record as <strong>VOID</strong>. It can&apos;t be paid or reinstated, and its number is
          never reused. To bill the family a different amount, create a new invoice.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          required
          label="Reason for voiding"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, VOID_REASON_MAX))}
          placeholder="e.g. Duplicate of MNM-2026-00061; family withdrew before term"
          helperText={`Kept for your records. The family never sees it. ${reason.length}/${VOID_REASON_MAX}`}
          inputProps={{ maxLength: VOID_REASON_MAX, 'aria-label': 'Reason for voiding' }}
        />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={notifyFamily}
              onChange={(e) => setNotifyFamily(e.target.checked)}
              inputProps={{ 'aria-label': 'Email the family that this invoice is void' }}
            />
          }
          label="Email the family that this invoice is void"
        />
        <FormHelperText sx={{ ml: 0 }}>
          They&apos;ll be told no payment is due. The reason isn&apos;t included.
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Keep invoice
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!valid || loading}
          onClick={() => onConfirm({ reason: trimmed, notifyFamily })}
        >
          {loading ? 'Voiding…' : 'Void invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
